// Keeps an Ops user's place when the inactivity lock signs them out mid-task:
// the route they were on plus whatever they had typed, replayed only when the
// same Cognito user signs back in.

const STORAGE_KEY = 'bhima-store.restore';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
const SKIP_PATHS = ['/login', '/signup', '/forgot-pin'];

// Live screens publish their in-progress state here; read only at save time.
const providers = new Map();
// Drafts claimed at sign-in, waiting for their screen to mount and take them.
let pendingDrafts = null;

export function registerDraftProvider(key, getter) {
  providers.set(key, getter);
  return () => {
    if (providers.get(key) === getter) providers.delete(key);
  };
}

function collectDrafts() {
  const drafts = {};
  providers.forEach((getter, key) => {
    try {
      const value = getter();
      if (value !== undefined && value !== null) drafts[key] = value;
    } catch {
      // A screen whose state will not serialise simply is not restored.
    }
  });
  return drafts;
}

export function saveRestorePoint(sub) {
  if (!sub) return;
  const path = `${window.location.pathname}${window.location.search}`;
  if (SKIP_PATHS.some((p) => path.startsWith(p))) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ sub, path, savedAt: Date.now(), drafts: collectDrafts() })
    );
  } catch {
    // Quota or private mode — resuming is best-effort, never blocks sign-out.
  }
}

export function clearRestorePoint() {
  pendingDrafts = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

// Call once right after a successful login. Returns the route to resume on, and
// arms the saved drafts for their screens. A different user gets nothing and
// the stored point is dropped either way, so it is never replayed twice.
export function claimRestorePoint(sub) {
  let point = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    point = raw ? JSON.parse(raw) : null;
  } catch {
    point = null;
  }
  clearRestorePoint();
  if (!point || !sub || point.sub !== sub) return null;
  if (Date.now() - (point.savedAt || 0) > MAX_AGE_MS) return null;
  pendingDrafts = point.drafts || null;
  return point.path || null;
}

export function takeDraft(key) {
  if (!pendingDrafts || !(key in pendingDrafts)) return undefined;
  const value = pendingDrafts[key];
  delete pendingDrafts[key];
  return value;
}
